import type { MCPEventType, MCPStreamEvent } from "@/types";

const normalizeEventName = (name: string): MCPEventType => {
  const normalized = name.trim().toLowerCase();

  if (normalized === "done" || normalized === "complete") return "done";
  if (normalized === "error" || normalized === "failed") return "error";
  if (normalized === "delta" || normalized === "chunk") return "delta";
  if (normalized === "log" || normalized === "info") return "log";
  if (normalized === "open" || normalized === "ready") return "open";

  return "message";
};

const safeJsonParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

/**
 * Parse a buffered Server-Sent Events payload into discrete events.
 * Returns the parsed events plus any trailing remainder that still
 * needs more data to form a full event.
 */
export const parseSseChunk = (buffer: string): { events: MCPStreamEvent[]; buffer: string } => {
  const blocks = buffer.split("\n\n");
  const remainder = blocks.pop() ?? "";

  const events: MCPStreamEvent[] = blocks
    .map(block => block.trim())
    .filter(Boolean)
    .map(block => parseEventBlock(block))
    .filter((event): event is MCPStreamEvent => Boolean(event));

  return { events, buffer: remainder };
};

const parseEventBlock = (block: string): MCPStreamEvent | null => {
  const lines = block.split(/\r?\n/);
  let eventName = "message";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim() || eventName;
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  }

  const dataString = dataLines.join("\n");
  const parsed = dataString ? safeJsonParse(dataString) : undefined;

  return {
    type: normalizeEventName(eventName),
    data: parsed ?? dataString,
    raw: block,
  };
};
